// src/Calendar.js
import React from 'react';
import { getDayInfo } from './finnishDays';
import { formatDate, isPast, isToday } from '../../utils/dateUtils';
import UiIcon from '../ui/UiIcon';


const Calendar = ({ puzzles, onSelectPuzzle, onCreatePuzzle, month, onMonthChange }) => {
  // Use month from props if provided, otherwise use local state
  const currentMonth = month || new Date();

  const changeMonth = (newMonth) => {
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };
  
  const getDays = (date) => {
    const year = date.getFullYear();
    const monthNum = date.getMonth();
    const firstDay = new Date(year, monthNum, 1);
    const lastDay = new Date(year, monthNum + 1, 0);
    const days = [];
    
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const getPuzzle = (date) => {
    const dateStr = formatDate(date);
    return puzzles.find(p => p.id === dateStr);
  };

  const getStatus = (dateStr, puzzle) => {
    if (!puzzle) return 'empty';

    // Check completeness FIRST before date-based status
    // A puzzle is incomplete if it has missing clues or invalid words
    if (puzzle.words && puzzle.words.length > 0) {
      const hasInvalidWords = puzzle.words.some(word => {
        // Check if answer is missing or empty
        return !word.answer || word.answer.trim() === '';
      });
      const hasMissingClues = puzzle.words.some(word => !word.clue || word.clue.trim() === '');

      // If incomplete, always show as unfinished (red) regardless of date
      if (hasInvalidWords || hasMissingClues) {
        return 'unfinished';
      }
    }

    // If puzzle is complete, return status based on date
    if (isToday(dateStr)) return 'today';
    if (isPast(dateStr)) return 'past';
    return puzzle.ready ? 'ready' : 'draft';
  };

  const getIndicator = (status, puzzle) => {
    const icons = { today: 'today', past: 'check_circle', ready: 'event_available', unfinished: 'warning', draft: 'edit' };
    if (!icons[status]) return null;
    return <UiIcon name={icons[status]} />;
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button onClick={() => changeMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="btn-secondary" aria-label="Previous month"><UiIcon name="chevron_left" /></button>
        <h2>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
        <button onClick={() => changeMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="btn-secondary" aria-label="Next month"><UiIcon name="chevron_right" /></button>
      </div>

      <div className="calendar-grid">
        {getDays(currentMonth).map(date => {
          const dateStr = formatDate(date);
          const puzzle = getPuzzle(date);
          const status = getStatus(dateStr, puzzle);
          const info = getDayInfo(date);
          const dayClass = info ? (info.isHoliday ? 'holiday' : info.isFlag ? 'flag' : 'notable') : '';

          return (
            <div
              key={dateStr}
              className={`calendar-day ${status} ${dayClass}`}
              title={info ? info.title : undefined}
              onClick={() => puzzle ? onSelectPuzzle(puzzle) : onCreatePuzzle(dateStr)}
            >
              <div className="day-number">{date.getDate()}</div>
              {info && (
                <div className="day-special">
                  {info.isFlag && <UiIcon name="flag" />}
                  <span className="day-special-name">{info.label}</span>
                </div>
              )}
              <div className="puzzle-indicator">{getIndicator(status, puzzle)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;